/**
 * /transport/international/apply/thanks — landing after the $9
 * Stripe Checkout completes. Polls `/me/transport/international/status`
 * to flip from `pending_payment` → `pending_review` so the user
 * sees confirmation rather than a stale state.
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, ClockIcon as Clock } from "@phosphor-icons/react";

export default function InternationalTransportApplyThanks() {
  const [search] = useSearchParams();
  const appId = search.get("app_id");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const r = await api.get("/me/transport/international/status");
        if (!cancelled) setStatus(r.data);
        if (r.data?.application_fee_paid || tries >= 8) return;
      } catch (_) {
        /* unauth — that's fine, user may not be logged in */
      }
      setTimeout(poll, 1500);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const paid = status?.application_fee_paid;
  return (
    <AppShell>
      <div
        className="max-w-2xl mx-auto px-4 py-16 text-center"
        data-testid="intl-apply-thanks"
      >
        {paid ? (
          <>
            <CheckCircle
              size={56}
              weight="duotone"
              className="text-[#1A6E36] mx-auto mb-4"
            />
            <h1 className="font-spectral text-4xl tracking-tight">
              Application received
            </h1>
            <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed max-w-md mx-auto">
              Thank you. Your $9 verification fee has been processed. We&rsquo;ll
              verify your credentials and get back to you within 5
              business days at the email you provided. Remember &mdash; your
              first lead is free once you&rsquo;re approved.
            </p>
          </>
        ) : (
          <>
            <Clock
              size={56}
              weight="duotone"
              className="text-[#8A6A00] mx-auto mb-4"
            />
            <h1 className="font-spectral text-4xl tracking-tight">
              Finalizing your application…
            </h1>
            <p className="text-[14px] text-[#5C5C5C] mt-3 leading-relaxed max-w-md mx-auto">
              Hold tight &mdash; we&rsquo;re confirming your payment with Stripe.
              This page will update automatically.
            </p>
          </>
        )}
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/dashboard">
            <Button className="rounded-full bg-crimson hover:bg-ink text-white">
              Go to dashboard
            </Button>
          </Link>
          <Link to="/transport/international">
            <Button variant="outline" className="rounded-full">
              View directory
            </Button>
          </Link>
        </div>
        <p className="text-[11px] text-[#8B8B8B] mt-10 max-w-md mx-auto leading-relaxed">
          Reference ID: <code className="text-[10px]">{appId || "—"}</code>
        </p>
      </div>
    </AppShell>
  );
}
